import React from 'react';
import { formatNumber } from 'libphonenumber-js';
import Modal from '@common/components/ConstellationV10Modal';
import { months } from '@common/paymentsUtils/dateUtils';
import { validateAllInputs } from '../validation';
import userPersonas from '@common/constants/userPersonas';
import errorMessages from '@common/constants/errorMessages';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormField,
  FormHelp,
  Input,
  Label,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import UnitedStatesSelect from '@common/components/UnitedStatesSelect';
import MaskedInput from '@common/components/ConstellationMaskedInput';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

type Props = {
  companyName: string;
  companyDbaName: string;
  companyTaxId: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  verifiedPhoneNumber: string;
  dobMonth: string;
  dobDay: string;
  dobYear: string;
  ssn: string;
  editMode: boolean;
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZip: string;
  onSubmit: (identityVerificationInfo: { identityVerificationInfo: Record<string, unknown> }) => Promise<unknown>;
  onBack: (info: Record<string, unknown>) => void;
  onCancel: () => void;
  onClose: () => void;
  selectedOption: string;
  emergencyMode: boolean;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
};

const TaxInfoInputPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const initialIdVerificationInfo = {
    companyName: { value: props.companyName ?? '', error: '' },
    companyDbaName: { value: props.companyDbaName ?? '', error: '' },
    companyTaxId: { value: props.companyTaxId ?? '', error: '' },
    firstName: { value: props.firstName ?? '', error: '' },
    lastName: { value: props.lastName ?? '', error: '' },
    address: { value: props.address ?? '', error: '' },
    city: { value: props.city ?? '', error: '' },
    state: { value: props.state ?? '', error: '' },
    zip: { value: props.zip ?? '', error: '' },
    phoneNumber: { value: props.phoneNumber || props.verifiedPhoneNumber || '', error: '' },
    dobMonth: { value: props.dobMonth ?? '', error: '' },
    dobDay: { value: props.dobDay ?? '', error: '' },
    dobYear: { value: props.dobYear ?? '', error: '' },
    ssn: { value: props.ssn ?? '', error: '' },
    companyAddress: { value: props.companyAddress ?? '', error: '' },
    companyCity: { value: props.companyCity ?? '', error: '' },
    companyState: { value: props.companyState ?? '', error: '' },
    companyZip: { value: props.companyZip ?? '', error: '' },
  };

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [idVerificationInfo, setIdVerificationInfo] = React.useState<Record<string, any>>(initialIdVerificationInfo);

  const {
    companyName,
    companyDbaName,
    companyTaxId,
    firstName,
    lastName,
    address,
    city,
    state,
    zip,
    phoneNumber,
    dobMonth,
    dobDay,
    dobYear,
    ssn,
    companyAddress,
    companyCity,
    companyState,
    companyZip,
  } = idVerificationInfo;

  const onInputChange = (e): void => {
    setIdVerificationInfo({
      ...idVerificationInfo,
      [e.currentTarget.dataset.statekey]: {
        value: e.currentTarget.value,
        error: '',
      },
    });
  };

  const onMaskedFieldInputChange = (e): void => {
    setIdVerificationInfo({
      ...idVerificationInfo,
      [e.currentTarget.dataset.statekey]: {
        value: e.currentTarget.value.replace(/[^\d]/g, ''),
        error: '',
      },
    });
  };

  const handleSubmit = async (): Promise<any> => {
    if (!validateAllInputs({ props, state: idVerificationInfo, setIdVerificationInfo })) {
      return;
    }
    let businessFields: Record<string, string> = {
      companyName: '',
      companyDbaName: '',
      companyTaxId: '',
      companyAddress: '',
      companyCity: '',
      companyState: '',
      companyZip: '',
    };
    if (props.selectedOption === 'single_member_llc' || props.selectedOption === 'multi_member_llc') {
      businessFields = {
        ...businessFields,
        companyName: companyName.value.trim(),
        companyDbaName: companyDbaName.value.trim(),
        companyTaxId: companyTaxId.value.trim(),
      };
      if (props.selectedOption === 'multi_member_llc') {
        businessFields = {
          ...businessFields,
          companyAddress: companyAddress.value.trim(),
          companyCity: companyCity.value.trim(),
          companyState: companyState.value,
          companyZip: companyZip.value.trim(),
        };
      }
    }

    setIsLoading(true);
    const response = await props.onSubmit({
      identityVerificationInfo: {
        editMode: false,
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        address: address.value.trim(),
        city: city.value.trim(),
        state: state.value,
        zip: zip.value.trim(),
        phoneNumber: formatNumber({ country: 'US', phone: phoneNumber.value }, 'NATIONAL'),
        dobMonth: dobMonth.value,
        dobDay: dobDay.value.trim(),
        dobYear: dobYear.value.trim(),
        ssn: ssn.value.trim(),
        ...businessFields,
        selectedTaxVerificationType: props.selectedOption,
      },
    });

    const res = response as { error?: boolean; phoneNumberInvalid?: boolean } | null;
    if (res && res.error) {
      setIsLoading(false);
      setIdVerificationInfo({
        ...idVerificationInfo,
        phoneNumber: res.phoneNumberInvalid
          ? {
              ...phoneNumber,
              error: errorMessages.PHONE_NUMBER_INVALID,
            }
          : { ...phoneNumber },
      });
    } else {
      IreneOnboardingCSEvents.emitSubmitFromTaxVerificationPageEvent({
        currentProperty: props.currentProperty,
        user: props.user,
      });
    }
  };

  const onBack = (): void => {
    props.onBack({
      companyName: companyName.value?.trim(),
      companyDbaName: companyDbaName.value?.trim(),
      companyTaxId: companyTaxId.value?.trim(),
      firstName: firstName.value?.trim(),
      lastName: lastName.value?.trim(),
      address: address.value?.trim(),
      city: city.value?.trim(),
      state: state.value,
      zip: zip.value?.trim(),
      phoneNumber: phoneNumber.value?.trim(),
      dobMonth: dobMonth.value,
      dobDay: dobDay.value?.trim(),
      dobYear: dobYear.value?.trim(),
      ssn: ssn.value?.trim(),
      companyAddress: companyAddress.value?.trim(),
      companyCity: companyCity.value?.trim(),
      companyState: companyState.value,
      companyZip: companyZip.value?.trim(),
    });
  };

  const onCancel = (): void => {
    props.onCancel();
  };

  const renderMonthOptions = (): Array<JSX.Element> | null =>
    months.map((monthOption, index) => (
      <option key={monthOption} value={(index + 1).toString()}>
        {monthOption}
      </option>
    ));

  const renderPhoneNumberReminder = (): JSX.Element | null => {
    const hasVerifiedPhoneNumber = props.verifiedPhoneNumber;
    const enteredNumberMatchesVerifiedNumber = props.verifiedPhoneNumber === phoneNumber.value;

    if (hasVerifiedPhoneNumber && enteredNumberMatchesVerifiedNumber && !phoneNumber.error) {
      return (
        <Paragraph textStyle="fineprint">
          This is the phone number you used to set up this property. Changing it here will change it for your property.
        </Paragraph>
      );
    }
    return null;
  };

  const renderIndividualTaxForm = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
        }}
      >
        <Paragraph textStyle="body-lg-bold">Verify your individual tax information</Paragraph>

        <Paragraph textStyle="body-sm">Enter your information as it appears on your tax documents.</Paragraph>
        <FormField
          control={
            <Input
              fluid
              size="md"
              type="text"
              className="fs-exclude"
              data-statekey="firstName"
              value={firstName.value}
              error={firstName.error}
              onChange={onInputChange}
            />
          }
          description={firstName.error && <FormHelp error>{firstName.error}</FormHelp>}
          label={<Label>Legal first name</Label>}
        />
        <FormField
          control={
            <Input
              fluid
              size="md"
              type="text"
              data-statekey="lastName"
              className="fs-exclude"
              value={lastName.value}
              error={lastName.error}
              onChange={onInputChange}
            />
          }
          description={lastName.error && <FormHelp error>{lastName.error}</FormHelp>}
          label={<Label>Legal last name</Label>}
        />
        <FormField
          control={
            <Input
              fluid
              size="md"
              data-statekey="address"
              className="fs-exclude"
              value={address.value}
              error={address.error}
              onChange={onInputChange}
            />
          }
          description={
            <>
              {address.error && <FormHelp error>{address.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Not a post office box or the rental property address.</Paragraph>
            </>
          }
          label={<Label>Current street address</Label>}
        />

        <Box css={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 'tighter' }}>
          <FormField
            control={
              <Input
                fluid
                size="md"
                data-statekey="city"
                className="fs-exclude"
                value={city.value}
                error={city.error}
                onChange={onInputChange}
              />
            }
            description={city.error && <FormHelp error>{city.error}</FormHelp>}
            label={<Label>City</Label>}
          />
          <FormField
            control={
              <UnitedStatesSelect
                data-statekey="state"
                className="fs-exclude"
                onChange={onInputChange}
                value={state.value}
                error={state.error}
              />
            }
            description={state.error && <FormHelp error>{state.error}</FormHelp>}
            label={<Label>State</Label>}
          />
          <FormField
            control={
              <Input
                fluid
                size="md"
                data-statekey="zip"
                className="fs-exclude"
                value={zip.value}
                error={zip.error}
                onChange={onInputChange}
              />
            }
            description={zip.error && <FormHelp error>{zip.error}</FormHelp>}
            label={<Label>ZIP code</Label>}
          />
        </Box>

        <FormField
          control={
            <MaskedInput
              statekey="phoneNumber"
              className="fs-exclude"
              type="tel"
              placeholder="Enter phone number"
              value={phoneNumber.value}
              error={phoneNumber.error}
              mask="(999) 999-9999"
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {phoneNumber.error && <FormHelp error>{phoneNumber.error}</FormHelp>}
              {renderPhoneNumberReminder()}
            </>
          }
          label={<Label>Phone number</Label>}
        ></FormField>

        <Box>
          <Label css={{ paddingBottom: 'default' }}>Date of birth</Label>
          <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'tighter' }}>
            <FormField
              control={
                <Select
                  data-statekey="dobMonth"
                  className="fs-exclude"
                  value={dobMonth.value}
                  error={dobMonth.error}
                  onChange={onInputChange}
                >
                  <option value="" />
                  {renderMonthOptions()}
                </Select>
              }
              description={dobMonth.error && <FormHelp error>{dobMonth.error}</FormHelp>}
            />
            <FormField
              control={
                <Input
                  className="fs-exclude"
                  data-statekey="dobDay"
                  placeholder="DD"
                  value={dobDay.value}
                  error={dobDay.error}
                  onChange={onInputChange}
                />
              }
              description={dobDay.error && <FormHelp error>{dobDay.error}</FormHelp>}
            />
            <FormField
              control={
                <MaskedInput
                  className="fs-exclude"
                  statekey="dobYear"
                  placeholder="YYYY"
                  value={dobYear.value}
                  error={dobYear.error}
                  onChange={onMaskedFieldInputChange}
                  mask="9999"
                />
              }
              description={dobYear.error && <FormHelp error>{dobYear.error}</FormHelp>}
            />
          </Box>
        </Box>
        <FormField
          control={
            <MaskedInput
              className="fs-exclude"
              statekey="ssn"
              mask="999-99-9999"
              placeholder="XXX&#8209;XX&#8209;XXXX"
              value={ssn.value}
              error={ssn.error}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {ssn.error && <FormHelp error>{ssn.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Your full Social Security number is needed for tax purposes.</Paragraph>
            </>
          }
          label={<Label>Social Security number</Label>}
        ></FormField>
      </Box>
    );
  };

  const renderBusinessTaxForm = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
        }}
      >
        <Paragraph textStyle="body-lg-bold">
          {props.selectedOption === 'single_member_llc'
            ? 'Verify your single LLC tax information'
            : 'Verify your multi-member LLC or corporation tax information'}
        </Paragraph>

        <Paragraph textStyle="body-sm">Enter the business&apos;s information as it appears on tax documents.</Paragraph>

        <FormField
          control={
            <Input
              data-statekey="companyName"
              className="fs-exclude"
              placeholder="Enter the business name"
              value={companyName.value}
              error={companyName.error}
              onChange={onInputChange}
            />
          }
          description={companyName.error && <FormHelp error>{companyName.error}</FormHelp>}
          label={<Label>Business name</Label>}
        />

        <FormField
          control={
            <Input
              data-statekey="companyDbaName"
              className="fs-exclude"
              placeholder="Enter DBA name"
              value={companyDbaName.value}
              error={companyDbaName.error}
              onChange={onInputChange}
            />
          }
          description={
            <>
              {companyDbaName.error && <FormHelp error>{companyDbaName.error}</FormHelp>}
              <Paragraph textStyle="fineprint">
                Fill in only if the business operates under a name other than the registered name.
              </Paragraph>
            </>
          }
          label={
            <Label>
              Doing Business As (DBA) name&nbsp;
              <i>(if applicable)</i>
            </Label>
          }
        />
        <FormField
          control={
            <MaskedInput
              statekey="companyTaxId"
              className="fs-exclude"
              mask="99-9999999"
              placeholder="XX-XXXXXXX"
              value={companyTaxId.value}
              error={companyTaxId.error}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {companyTaxId.error && <FormHelp error>{companyTaxId.error}</FormHelp>}
              <Paragraph textStyle="fineprint">
                Check your tax documents or business loan applications, or call the IRS.
              </Paragraph>
            </>
          }
          label={<Label>Employer Identification Number (EIN)</Label>}
        />

        {props.selectedOption === 'multi_member_llc' && (
          <>
            <FormField
              control={
                <Input
                  data-statekey="companyAddress"
                  className="fs-exclude"
                  placeholder="Enter street address"
                  value={companyAddress.value}
                  error={companyAddress.error}
                  onChange={onInputChange}
                />
              }
              description={
                <>
                  {companyAddress.error && <FormHelp error>{companyAddress.error}</FormHelp>}
                  <Paragraph textStyle="fineprint">Not a post office box or the rental property address.</Paragraph>
                </>
              }
              label={<Label>Business street address</Label>}
            />

            <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'tighter' }}>
              <FormField
                control={
                  <Input
                    data-statekey="companyCity"
                    className="fs-exclude"
                    placeholder="Enter city"
                    value={companyCity.value}
                    error={companyCity.error}
                    onChange={onInputChange}
                  />
                }
                description={companyCity.error && <FormHelp error>{companyCity.error}</FormHelp>}
                label={<Label>City</Label>}
              />

              <FormField
                control={
                  <UnitedStatesSelect
                    data-statekey="companyState"
                    className="fs-exclude prs"
                    onChange={onInputChange}
                    value={companyState.value}
                    error={companyState.error}
                  />
                }
                description={companyState.error && <FormHelp error>{companyState.error}</FormHelp>}
                label={<Label>State</Label>}
              />
              <FormField
                control={
                  <Input
                    data-statekey="companyZip"
                    className="fs-exclude"
                    placeholder="Zip"
                    value={companyZip.value}
                    error={companyZip.error}
                    onChange={onInputChange}
                  />
                }
                description={companyZip.error && <FormHelp error>{companyZip.error}</FormHelp>}
                label={<Label>ZIP code</Label>}
              />
            </Box>
            <Divider />
            <Paragraph textStyle="body-lg-bold">Owner information</Paragraph>
          </>
        )}

        <FormField
          control={
            <Input
              data-statekey="firstName"
              className="fs-exclude"
              placeholder="Enter Owner first name"
              value={firstName.value}
              error={firstName.error}
              onChange={onInputChange}
            />
          }
          description={firstName.error && <FormHelp error>{firstName.error}</FormHelp>}
          label={<Label>Owner first name</Label>}
        />

        <FormField
          control={
            <Input
              data-statekey="lastName"
              className="fs-exclude"
              placeholder="Enter Owner last name"
              value={lastName.value}
              error={lastName.error}
              onChange={onInputChange}
            />
          }
          description={lastName.error && <FormHelp error>{lastName.error}</FormHelp>}
          label={<Label>Owner last name</Label>}
        />

        <FormField
          control={
            <Input
              data-statekey="address"
              className="fs-exclude"
              placeholder="Enter street address"
              value={address.value}
              error={address.error}
              onChange={onInputChange}
            />
          }
          description={
            <>
              {address.error && <FormHelp error>{address.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Not a post office box or the rental property address.</Paragraph>
            </>
          }
          label={<Label>Owner street address</Label>}
        />

        <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'tighter' }}>
          <FormField
            control={
              <Input
                data-statekey="city"
                className="fs-exclude"
                placeholder="Enter city"
                value={city.value}
                error={city.error}
                onChange={onInputChange}
              />
            }
            description={city.error && <FormHelp error>{city.error}</FormHelp>}
            label={<Label>City</Label>}
          />

          <FormField
            control={
              <UnitedStatesSelect
                data-statekey="state"
                className="fs-exclude"
                onChange={onInputChange}
                value={state.value}
                error={state.error}
              />
            }
            description={state.error && <FormHelp error>{state.error}</FormHelp>}
            label={<Label>State</Label>}
          />

          <FormField
            control={
              <Input
                data-statekey="zip"
                className="fs-exclude"
                placeholder="Zip"
                value={zip.value}
                error={zip.error}
                onChange={onInputChange}
              />
            }
            description={zip.error && <FormHelp error>{zip.error}</FormHelp>}
            label={<Label>Owner ZIP code</Label>}
          />
        </Box>

        <FormField
          control={
            <MaskedInput
              statekey="phoneNumber"
              className="fs-exclude"
              type="tel"
              placeholder="Enter phone number"
              value={phoneNumber.value}
              error={phoneNumber.error}
              mask="(999) 999-9999"
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {phoneNumber.error && <FormHelp error>{phoneNumber.error}</FormHelp>}
              {renderPhoneNumberReminder()}
            </>
          }
          label={<Label>Phone number</Label>}
        />

        <Box>
          <Label>Date of birth</Label>
        </Box>
        <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'tighter' }}>
          <FormField
            control={
              <Select
                data-statekey="dobMonth"
                className="fs-exclude"
                value={dobMonth.value}
                error={dobMonth.error}
                onChange={onInputChange}
              >
                <option value="" />
                {renderMonthOptions()}
              </Select>
            }
            description={dobMonth.error && <FormHelp error>{dobMonth.error}</FormHelp>}
          />

          <FormField
            control={
              <Input
                className="fs-exclude"
                data-statekey="dobDay"
                placeholder="DD"
                value={dobDay.value}
                error={dobDay.error}
                onChange={onInputChange}
              />
            }
            description={dobDay.error && <FormHelp error>{dobDay.error}</FormHelp>}
          />

          <FormField
            control={
              <MaskedInput
                className="fs-exclude"
                statekey="dobYear"
                placeholder="yyyy"
                value={dobYear.value}
                error={dobYear.error}
                onChange={onMaskedFieldInputChange}
                mask="9999"
              />
            }
            description={dobYear.error && <FormHelp error>{dobYear.error}</FormHelp>}
          />
        </Box>

        <FormField
          control={
            <MaskedInput
              className="fs-exclude"
              statekey="ssn"
              mask="999-99-9999"
              placeholder="XXX&#8209;XX&#8209;XXXX"
              value={ssn.value}
              error={ssn.error}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {ssn.error && <FormHelp error>{ssn.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Your full Social Security number is needed for tax purposes.</Paragraph>
            </>
          }
          label={<Label>Social Security number</Label>}
        />
      </Box>
    );
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={props.emergencyMode ? undefined : props.onClose}
      header="Payment setup"
      body={<Box>{props.selectedOption === 'individual' ? renderIndividualTaxForm() : renderBusinessTaxForm()}</Box>}
      footer={
        <ButtonGroup aria-label="Tax information actions">
          <TextButton onClick={onBack}>Back</TextButton>
          <Button tone="brand" emphasis="filled" onClick={handleSubmit} loading={isLoading}>
            {props.editMode ? 'Update' : 'Continue'}
          </Button>
          {props.editMode && (
            <Button onClick={onCancel} tone="brand" emphasis="outlined">
              Cancel
            </Button>
          )}
        </ButtonGroup>
      }
    />
  );
};

TaxInfoInputPage.defaultProps = {
  companyName: '',
  companyDbaName: '',
  companyTaxId: '',
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phoneNumber: '',
  verifiedPhoneNumber: '',
  dobMonth: '',
  dobDay: '',
  dobYear: '',
  ssn: '',
  editMode: false,
  companyAddress: '',
  companyCity: '',
  companyState: '',
  companyZip: '',
};

export default TaxInfoInputPage;
